/**
 * Code splitting. The title screen needs only a small part of the game, so the gameplay scene
 * (world, HUD, menus, sprites) and the cutscene player (script, illustrations, end credits) are
 * separate chunks. preloadGame() fetches both in the background as soon as the game starts,
 * which also lets the service worker cache them for offline play. Once loaded they are used
 * synchronously, exactly as when the game was a single bundle.
 */
import type { Scene } from '../engine/app';
import type { Game } from '../game/game';

/** A dynamically imported module, also available synchronously once it has loaded. */
class Chunk<T> {
  loaded: T | null = null;

  constructor(private readonly importer: () => Promise<T>) {}

  load(): Promise<T> {
    return this.importer().then((m) => (this.loaded = m));
  }
}

export const worldChunk = new Chunk(() => import('./world-scene'));
export const cutsceneChunk = new Chunk(() => import('./cutscene'));

/** Fetch the code-split chunks in the background. Failures are ignored: the real load retries. */
export function preloadGame(): void {
  for (const chunk of [worldChunk, cutsceneChunk]) chunk.load().catch(() => undefined);
}

/** Opaque black scene that keeps the scenes below it paused (e.g. while the world loads). */
export class BlankScene implements Scene {
  readonly opaque = true;
  update(): void {}
  render(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  }
}

/** Play a cutscene; resolves when it ends or is skipped. */
export function playCutscene(game: Game, id: string): Promise<void> {
  return pushFrom(game, cutsceneChunk, (m, done) => new m.CutsceneScene(game, id, done));
}

/** Roll the end credits; resolves when they end. */
export function playCredits(game: Game): Promise<void> {
  return pushFrom(game, cutsceneChunk, (m, done) => new m.CreditsScene(game, done));
}

/**
 * Push a scene from a code-split chunk; resolves when the scene calls `done`. Once the chunk
 * has loaded the scene is pushed right away; until then a BlankScene holds the game. If the
 * chunk can't be loaded (offline before it was ever fetched), the scene is skipped.
 */
function pushFrom<T>(game: Game, chunk: Chunk<T>, make: (m: T, done: () => void) => Scene): Promise<void> {
  const push = (m: T): Promise<void> => new Promise((done) => game.app.push(make(m, done)));
  if (chunk.loaded) return push(chunk.loaded);
  const hold = new BlankScene();
  game.app.push(hold);
  return chunk.load().then(
    (m) => {
      const ended = push(m);
      game.app.remove(hold);
      return ended;
    },
    (err: unknown) => {
      console.error('Skipping a scene that failed to load:', err);
      game.app.remove(hold);
    },
  );
}
